import React, { useContext, useEffect } from 'react';
import { ZoomMtg } from '@zoom/meetingsdk';
import { useAuthContext } from '../../context/AuthContextProvider';
import { useModuleContext } from '../../context/ModuleProvider';
import { MeetingContext } from '../../context/MeetingContextProvider';
import { useHistory } from 'react-router-dom';
import axiosInstance from '../../axiosapi';

function ZoomSDK() {
  let { currentUser, streakUpdated, setStreakUpdated } = useAuthContext();
  const { meeting, setMeeting } = useContext(MeetingContext);
  const { url } = useModuleContext();
  const history = useHistory();

  const leaveURL = process.env.REACT_APP_CLIENT_URL;

  const getMdkSignature = async (e) => {
    if (e) {
      e.preventDefault();
    }

    let role;
    if (meeting.isWebinar) {
      role = currentUser.role === 'student' ? 0 : 1;
    } else {
      role = currentUser.role === 'student' ? 0 : 1;
    }

    try {
      const response = await axiosInstance({
        method: 'post',
        url: `${url}/zoom/getMdkSignatureEnhanced`,
        data: {
          meetingNumber: meeting.meetingId,
          role: role,
          isWebinar: meeting.isWebinar || false, // Send webinar flag to backend
        },
      });

      startMeeting(response.data.signature);
      handleUpdateStreak();
    } catch (err) {
      console.error('Error getting signature:', err);

      alert(`Failed to get access credentials: ${err.response.data.error || err.message || 'Unknown error'}`);
    }
  };

  const handleUpdateStreak = () => {
    if (currentUser && !streakUpdated) {
      axiosInstance.post(`${url}/user-streak?userId=${currentUser.id}`).then(() => {
        setStreakUpdated(true);
      });
    }
  };

  const startMeeting = (signature) => {
    ZoomMtg.preLoadWasm();
    ZoomMtg.prepareWebSDK();

    document.getElementById('zmmtg-root').style.display = 'block';

    // Common init options
    const initOptions = {
      leaveUrl: leaveURL,
      disablePreview: true,
      disableInvite: true,
      success: (success) => {
        console.log('SDK initialized successfully');
        joinSession(signature);
      },
      error: (error) => {
        console.error('Init error:', error);
        alert(`Failed to initialize Zoom SDK: ${error.message || 'Unknown error'}`);
      },
    };

    ZoomMtg.init(initOptions);
  };

  const joinSession = (signature) => {
    const joinOptions = {
      signature: signature,
      sdkKey: meeting.meetingMsdkKey,
      meetingNumber: meeting.meetingId,
      passWord: meeting.meetingPw,
      userName: `${currentUser.first_name} ${currentUser.middle_name} ${currentUser.last_name}`,
      userEmail: currentUser.email || 'user@user.com',
      success: (success) => {
        if (meeting.isWebinar && currentUser.role === 'student') {
          console.log('Joined webinar as attendee');
        }
      },
      error: (error) => {
        alert(`Failed to join ${meeting.isWebinar ? 'webinar' : 'meeting'}: ${error.message || 'Unknown error'}`);
      },
    };

    if (meeting.isWebinar && meeting.registrantId) {
      joinOptions.registrantToken = meeting.registrantToken;
      console.log('Using registrant token for webinar');
    }

    ZoomMtg.join(joinOptions);
  };

  useEffect(() => {
    // Check for required meeting/webinar data
    const hasRequiredData = meeting.meetingId && meeting.meetingMsdkKey && currentUser;

    if (hasRequiredData) {
      getMdkSignature();
    } else {
      history.replace('/');
    }
  }, [meeting, currentUser]);

  useEffect(() => {
    // Store original elements with null checks
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    const originalStylesheets = stylesheets.filter((sheet) => sheet.parentNode);

    const scripts = Array.from(document.querySelectorAll('script'));
    const originalScripts = scripts.filter((script) => script.parentNode);

    const styles = Array.from(document.querySelectorAll('style[type="text/css"]'));
    const originalStyles = styles.filter((style) => style.parentNode);

    // Remove elements safely
    originalStylesheets.forEach(function (stylesheet) {
      if (stylesheet.parentNode) {
        stylesheet.parentNode.removeChild(stylesheet);
      }
    });

    originalScripts.forEach(function (script) {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });

    originalStyles.forEach(function (style) {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    });

    const newStyle = document.createElement('style');
    newStyle.type = 'text/css';
    newStyle.innerHTML = `
    #zmmtg-root .dropdown.btn-group .participants-header__participants-pop-btn.ax-outline-blue-important.dropdown-toggle.btn.btn-default,
    #zmmtg-root .chat-header__chat-pop-btn.ax-outline-blue-important.dropdown-toggle.btn.btn-default{
      border: none;
    }

    #zmmtg-root .chat-receiver-list__receiver.dropdown-toggle.btn.btn-default{
      padding: 0 10px;
    }

    #zmmtg-root .dark-dropdown.new-LT__main-dropdown.custom-dropdown-menu.dropdown-menu{
        background-color: rgba(0, 0, 0, 0.8);
    }
      
    #zmmtg-root .dark-dropdown.new-LT__main-dropdown.custom-dropdown-menu.dropdown-menu li a{
        color: #fafbfc;
    }

    #zmmtg-root .meeting-info-container__wrapper{
      display: none;
    }
    
    #zmmtg-root .join-audio-by-phone {
      display: none;
      pointer-events: none;
    }

    #zmmtg-root .join-audio-by-phone-mobile {
      display: none !important;
      pointer-events: none;
    }

    /* Webinar-specific styles */
    ${
      meeting.isWebinar
        ? `
    #zmmtg-root .webinar-qa-container {
      /* Style Q&A section for webinars */
    }
    
    #zmmtg-root .webinar-poll-container {
      /* Style polling section for webinars */
    }
    `
        : ''
    }
    `;
    document.head.appendChild(newStyle);

    const link = document.createElement('link');
    link.href = 'https://source.zoom.us/3.12.0/css/bootstrap.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const link2 = document.createElement('link');
    link2.href = 'https://source.zoom.us/3.12.0/css/react-select.css';
    link2.rel = 'stylesheet';
    document.head.appendChild(link2);

    return () => {
      // Cleanup with null checks
      try {
        if (document.head.contains(link2)) {
          document.head.removeChild(link2);
        }
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
        if (document.head.contains(newStyle)) {
          document.head.removeChild(newStyle);
        }

        // Restore original elements
        originalStylesheets.forEach(function (stylesheet) {
          if (stylesheet && !document.head.contains(stylesheet)) {
            document.head.appendChild(stylesheet);
          }
        });

        originalScripts.forEach(function (script) {
          if (script && !document.body.contains(script)) {
            const clonedScript = script.cloneNode(true);
            document.body.appendChild(clonedScript);
          }
        });

        originalStyles.forEach(function (style) {
          if (style && !document.head.contains(style)) {
            document.head.appendChild(style);
          }
        });
      } catch (error) {
        console.warn('Cleanup error:', error);
      }
    };
  }, [meeting.isWebinar]);

  window.addEventListener('popstate', function (event) {
    endZoomSession();
    setMeeting({
      meetingId: '',
      meetingPw: '',
      meetingMsdkKey: '',
      isWebinar: false,
      registrantId: '',
      registrantToken: '',
    });
  });

  function endZoomSession() {
    ZoomMtg.leaveMeeting({
      success: function (data) {
        console.log(`Leave ${meeting.isWebinar ? 'webinar' : 'meeting'} success`, data);
      },
      error: function (error) {
        console.error(`Leave ${meeting.isWebinar ? 'webinar' : 'meeting'} error`, error);
      },
    });
  }

  return (
    <div
      className={`zoomsdk ${meeting.isWebinar ? 'webinar-mode' : 'meeting-mode'}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100vw',
        height: '100vh',
      }}
    >
      <h1>Joining {meeting.isWebinar ? 'Webinar' : 'Meeting'}...</h1>
    </div>
  );
}

export default ZoomSDK;
